import * as THREE from 'three';
import type { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import type { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import type { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { FpsChromaticAberration } from './FpsChromaticAberration.js';
import { FpsMotionBlurPass } from './FpsMotionBlurPass.js';

/** Full-screen punchy grade + vignette (stylized BR look). */
export const FortniteGradeShader = {
  name: 'FortniteGrade',

  uniforms: {
    tDiffuse: { value: null },
    saturation: { value: 1.24 },
    contrast: { value: 1.08 },
    vignette: { value: 0.34 },
    /** Subtle RGB split at frame edges (BR-style CA). */
    aberration: { value: 0.0015 },
    /** Lift shadows slightly for a brighter stylized look. */
    lift: { value: 0.02 },
    /** Unsharp mask strength (0 = off). */
    sharpness: { value: 0.15 },
    /** Time/weather mood tint mixed after the base grade. */
    moodTint: { value: [1.0, 1.0, 1.0] },
    moodStrength: { value: 0.0 },
  },

  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,

  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float saturation;
    uniform float contrast;
    uniform float vignette;
    uniform float aberration;
    uniform float lift;
    uniform float sharpness;
    uniform vec3 moodTint;
    uniform float moodStrength;
    varying vec2 vUv;

    vec3 sat(vec3 c, float s) {
      float y = dot(c, vec3(0.299, 0.587, 0.114));
      return mix(vec3(y), c, s);
    }

    void main() {
      vec4 tex = texture2D(tDiffuse, vUv);
      vec2 dir = (vUv - 0.5) * 2.0;
      vec2 off = normalize(dir + 1e-5) * aberration * dot(dir, dir);
      float r = texture2D(tDiffuse, vUv + off).r;
      float g = tex.g;
      float b = texture2D(tDiffuse, vUv - off).b;
      vec3 rgb = vec3(r, g, b);
      rgb = pow(max(rgb, vec3(1e-5)), vec3(0.96));
      rgb = sat(rgb, saturation);
      rgb = (rgb - 0.5) * contrast + 0.5;
      // Warm highlights, cool shadows tone (subtle split-tone)
      float lum = dot(rgb, vec3(0.299, 0.587, 0.114));
      vec3 warm = vec3(1.04, 1.00, 0.92);
      vec3 cool = vec3(0.94, 0.99, 1.06);
      rgb *= mix(cool, warm, smoothstep(0.25, 0.85, lum));
      rgb += vec3(lift) * (1.0 - smoothstep(0.08, 0.72, lum));
      rgb = mix(rgb, rgb * moodTint, moodStrength);
      if (sharpness > 0.001) {
        vec2 px = vec2(0.00085, 0.00085);
        vec3 blur = (
          texture2D(tDiffuse, vUv + vec2(px.x, 0.0)).rgb +
          texture2D(tDiffuse, vUv - vec2(px.x, 0.0)).rgb +
          texture2D(tDiffuse, vUv + vec2(0.0, px.y)).rgb +
          texture2D(tDiffuse, vUv - vec2(0.0, px.y)).rgb
        ) * 0.25;
        rgb += (rgb - blur) * sharpness;
      }
      vec2 d = vUv - 0.5;
      float v = 1.0 - vignette * dot(d, d);
      rgb *= v;
      gl_FragColor = vec4(clamp(rgb, 0.0, 1.0), tex.a);
    }
  `,
};

const BLOOM_STRENGTH_NORMAL = 0.42;
const BLOOM_STRENGTH_PERF = 0.26;

/** Time-of-day bloom curve — golden hour boost, night dip, performance cap. */
export function getTimeOfDayBloomStrength(
  daylight: number,
  nightAmount: number,
  sunsetAmount: number,
  sunriseAmount: number,
  performanceMode: boolean,
): number {
  const base = performanceMode ? BLOOM_STRENGTH_PERF : BLOOM_STRENGTH_NORMAL;
  const goldenHour = Math.max(sunriseAmount, sunsetAmount);
  const noonBoost = daylight * (1 - goldenHour * 0.35);
  const curve = 0.76 + noonBoost * 0.14 + goldenHour * 0.32 - nightAmount * 0.24;
  return base * THREE.MathUtils.clamp(curve, 0.62, 1.28);
}

export type PostProcessingPipeline = {
  composer: EffectComposer;
  bloomPass: UnrealBloomPass;
  gradePass: ShaderPass;
  chromaticAberration: FpsChromaticAberration;
  motionBlurPass: FpsMotionBlurPass;
};

export type PostProcessingOptions = {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  width: number;
  height: number;
  bloomStrength: number;
  bloomEnabled: boolean;
};

/** Lazy-load EffectComposer stack to trim initial bundle parse cost. */
export async function createPostProcessingPipeline(
  opts: PostProcessingOptions,
): Promise<PostProcessingPipeline> {
  const [
    { EffectComposer },
    { RenderPass },
    { UnrealBloomPass },
    { OutputPass },
    { ShaderPass },
  ] = await Promise.all([
    import('three/addons/postprocessing/EffectComposer.js'),
    import('three/addons/postprocessing/RenderPass.js'),
    import('three/addons/postprocessing/UnrealBloomPass.js'),
    import('three/addons/postprocessing/OutputPass.js'),
    import('three/addons/postprocessing/ShaderPass.js'),
  ]);

  const composer = new EffectComposer(opts.renderer);
  composer.addPass(new RenderPass(opts.scene, opts.camera));

  const pr = opts.renderer.getPixelRatio();
  const bloomRes = new THREE.Vector2(
    Math.max(256, Math.floor(opts.width * pr * 0.5)),
    Math.max(256, Math.floor(opts.height * pr * 0.5)),
  );
  // Single bloom pass with higher threshold so terrain/characters stay readable
  // (double-bloom at strength 1.6 washed the frame near-white under ACES).
  const bloomPass = new UnrealBloomPass(bloomRes, opts.bloomStrength, 0.32, 0.88);
  bloomPass.enabled = opts.bloomEnabled;
  composer.addPass(bloomPass);

  const gradePass = new ShaderPass(FortniteGradeShader);
  composer.addPass(gradePass);

  const chromaticAberration = new FpsChromaticAberration();
  const motionBlurPass = new FpsMotionBlurPass();
  composer.addPass(chromaticAberration.pass);
  composer.addPass(motionBlurPass.pass);
  composer.addPass(new OutputPass());
  composer.setSize(opts.width, opts.height);

  return { composer, bloomPass, gradePass, chromaticAberration, motionBlurPass };
}
